import React from "react";
import { useLocation } from "react-router-dom";

export interface ErrorLogEntry {
  id: string;
  time: number;
  message: string;
  stack?: string;
  componentStack?: string;
  route?: string;
  tags?: Record<string, any>;
  meta?: Record<string, any>;
}

interface ErrorLoggerContextValue {
  logs: ErrorLogEntry[];
  addLog: (entry: Omit<ErrorLogEntry, "id" | "time">) => void;
  clear: () => void;
  setTags: (tags: Record<string, any>) => void;
  getTags: () => Record<string, any>;
}

const ErrorLoggerContext = React.createContext<
  ErrorLoggerContextValue | undefined
>(undefined);

export const useErrorLogger = () => {
  const ctx = React.useContext(ErrorLoggerContext);
  if (!ctx)
    throw new Error("useErrorLogger must be used within ErrorLoggerProvider");
  return ctx;
};

export const ErrorLoggerProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [logs, setLogs] = React.useState<ErrorLogEntry[]>([]);
  const tagsRef = React.useRef<Record<string, any>>({});
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;

  const addLog: ErrorLoggerContextValue["addLog"] = React.useCallback(
    (entry) => {
      const newEntry: ErrorLogEntry = {
        id: crypto.randomUUID(),
        time: Date.now(),
        route: pathname ?? undefined,
        ...entry,
        tags: { ...tagsRef.current, ...(entry.tags || {}) },
      };
      setLogs((prev) => [...prev.slice(-199), newEntry]);
      if (typeof window !== "undefined") {
        (window as any).__APP_ERROR_LOGS__ =
          (window as any).__APP_ERROR_LOGS__ || [];
        (window as any).__APP_ERROR_LOGS__.push(newEntry);
        (window as any).__LAST_APP_ERROR__ = newEntry;
        // eslint-disable-next-line no-console
        console.error("[ErrorCaptured]", newEntry);
      }
    },
    [pathname]
  );

  const clear = React.useCallback(() => setLogs([]), []);
  const setTags = React.useCallback((t: Record<string, any>) => {
    tagsRef.current = { ...tagsRef.current, ...t };
  }, []);
  const getTags = React.useCallback(() => ({ ...tagsRef.current }), []);

  React.useEffect(() => {
    setTags({ route: pathname, query: search?.toString() });
  }, [pathname, search, setTags]);

  // Global unhandled errors & promise rejections
  React.useEffect(() => {
    const onError = (event: ErrorEvent) => {
      addLog({
        message: event.message,
        stack: event.error?.stack,
        meta: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
        tags: { type: "window.error" },
      });
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason: any = event.reason;
      addLog({
        message: reason?.message || String(reason),
        stack: reason?.stack,
        meta: { reason },
        tags: { type: "unhandledrejection" },
      });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, [addLog]);

  return (
    <ErrorLoggerContext.Provider
      value={{ logs, addLog, clear, setTags, getTags }}
    >
      {children}
    </ErrorLoggerContext.Provider>
  );
};

export const useRegisterErrorTags = (
  tags: Record<string, any>,
  deps: React.DependencyList = []
) => {
  const { setTags } = useErrorLogger();
  React.useEffect(() => {
    setTags(tags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
